import stateClasses from "../data/stateClasses";
import { loadAPI as loadGoogleMapsApi } from "../features/googleMaps";
import GoogleMap from "./GoogleMap";

export default class AjaxArchive {
	
	constructor(el) {

		// selectors
		this.selectors = {
			filterForm: '.js-ajax-archive-form',
			resultsContainer: '.js-ajax-archive-results-container',
			paginateLink: '.js-paginate-link-ajax',
		}

		// elements
		this.el = el;
		this.filterFormEl = this.el.querySelector('.js-ajax-archive-form');
		this.resultsContainerEl = this.el.querySelector(this.selectors.resultsContainer);

		// settings
		this.debugMode = WP.env !== 'production';
		this.isFiltered = false;
		this.hasMapView = !!this.el.getAttribute('data-has-map-view');

		this.addEventListeners();

		if (this.hasMapView) {
			this.locations = [];
			this.map = null;

			this.gridView = this.el.querySelector('.js-ajax-archive-grid-view');
			this.mapView = this.el.querySelector('.js-ajax-archive-map-view');
			this.modeSwitcherInputs = this.el.querySelectorAll('.js-ajax-archive-mode-switcher');
			this.mapEl = this.el.querySelector('.js-ajax-archive-map');
			this.mobileInfoWindow = this.el.querySelector('.js-ajax-archive-mobile-info-window');

			// get prefilters
			this.prefilters = this.el.getAttribute('data-map-prefilters') ? JSON.parse(this.el.getAttribute('data-map-prefilters')) : [];

			// set initial mode
			this.mode = Array.from(this.modeSwitcherInputs).find(input => input.checked).value;

			// listen for changes on the mode switcher
			this.modeSwitcherInputs.forEach(input => {
				input.addEventListener('change', e => {
					this.changeMode(e.target.value);
				});
			});

			// init the map if we're in map mode already
			if (this.mode === 'map') {
				this.initMap();
			}
		}
	}

	debug(...message) {
		if (!this.debugMode) return;
			
		if (message.length > 1) {
			console.log('AjaxArchive:', ...message);
		} else {
			console.log('AjaxArchive:', message[0]);
		}
	}

	addEventListeners() {

		// filtering
		this.filterFormEl.addEventListener('submit', e => {
			e.preventDefault();

			this.isFiltered = true;

			this.setLoadingState(true);

			if (this.hasMapView) {
				if (this.mode === 'map') {
					this.updateMap();
				}
		
				if (this.mode === 'grid') {
					this.updateResultsContainer();
				}
			} else {
				this.updateResultsContainer();
			}
		});

		// pagination
		this.el.addEventListener('click', e => {

			const clickedPaginationLink = e.target.closest(this.selectors.paginateLink);

			if (!clickedPaginationLink) return;
			
			e.preventDefault();

			this.setLoadingState(true);
			
			// get the url of the clicked link
			const url = new URL(clickedPaginationLink.href);

			// try and set the current page - fallback to 1
			const _page = parseInt(url.searchParams.get('_page')) || 1;

			// if going to the first page, we dont need the _page param (we actively don't want it!)
			if (_page === 1) {
				this.debug(`Paginating to the first page...`);
				this.updateResultsContainer();
			} else {
				this.debug(`Paginating to page: ${_page}...`);
				this.updateResultsContainer({ _page });
			}
		});
	}

	setLoadingState(isLoading) {
		if (isLoading) {
			this.el.classList.add(stateClasses.isLoading);
		} else {
			this.el.classList.remove(stateClasses.isLoading);
		}
	}

	buildUrlParams(additionalParams = {}) {
		
		const formData = new FormData(this.filterFormEl);

		// remove the keyword filter if it's empty
		if (formData.get('filters[keyword]') === '') {
			formData.delete('filters[keyword]');
		}

		for (const [param, value] of Object.entries(additionalParams)) {
			formData.append(param, value);
		};
		
		const urlParams = new URLSearchParams(formData);

		return urlParams.toString();
	}

	updateUrl(additionalParams = {}) {
		this.debug('Updating URL...');

		const urlParams = this.buildUrlParams(additionalParams);
		const newUrl = `${window.location.pathname}?${urlParams}`;

		history.pushState({}, '', newUrl);
	}

	changeMode(mode) {
		this.mode = mode;

		this.setLoadingState(true);

		if (this.mode === 'map') {
			this.gridView.classList.add(stateClasses.isHidden);
			this.mapView.classList.remove(stateClasses.isHidden);

			if (this.map) {
				this.updateMap();
			} else {
				this.initMap();
			}
		}

		if (this.mode === 'grid') {
			this.gridView.classList.remove(stateClasses.isHidden);
			this.mapView.classList.add(stateClasses.isHidden);

			if (this.isFiltered) {
				this.updateResultsContainer();
			} else {
				this.setLoadingState(false);
				this.updateUrl();
			}
		}
	}

	async updateResultsContainer(additionalParams = {}) {
		
		const urlParams = this.buildUrlParams(additionalParams);
		
		try {
			const url = `${this.filterFormEl.getAttribute('action')}?${urlParams}`;
			const response = await fetch(url);
			const html = await response.text();

			// initialize the DOM parser
			const parser = new DOMParser();
		
			// parse the text
			const parsedHtml = parser.parseFromString(html, "text/html");
		
			const results = parsedHtml.querySelector(this.selectors.resultsContainer).innerHTML;
		
			this.resultsContainerEl.innerHTML = results;

			this.resultsContainerEl.scrollIntoView();

		} catch(error) {
			console.error('Failed to fetch page: ', error)
			alert('Soemthing went wrong. Please try again.');
		}

		// clear the loading state
		this.setLoadingState(false);

		// update the URL
		this.updateUrl(additionalParams);
	}

	async initMap() {

		const dynamicLocations = await this.getMapLocationsFromApi();
		const staticLocations = JSON.parse(this.mapEl.getAttribute('data-static-locations'));

		const initialLocations = dynamicLocations.concat(staticLocations);

		loadGoogleMapsApi().then(() => {

			// create the map
			this.map = new GoogleMap(this.mapEl, initialLocations);

			if (this.debugMode) {
				// store the map on the window object so we can access it later
				window.archiveMap = this.map;
			}

			// clear the loading state
			this.setLoadingState(false);

			// update the URL
			this.updateUrl();
		});
	}

	async updateMap() {

		// get the locations from the API
		const locations = await this.getMapLocationsFromApi();

		// clear the map and set the new locations
		this.map.removeMarkers('dynamic');
		this.map.addMarkers(locations);

		// clear the loading state
		this.setLoadingState(false);

		// update the URL
		this.updateUrl();
	}

	async getMapLocationsFromApi() {

		// create the url params string
		const urlParams = this.buildUrlParams({
			as_map_pins: 1,
			per_page: '-1', // force all locations to be returned
		});

		// fetch the locations from the API
		const url = `${window.location.origin}/wp-json/bid/v1/members/?${urlParams}`;
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				prefilters: this.prefilters,
			}),
		});
		const data = await response.json();

		return data;
	}
}